import { useTranslations } from '@vocab/react';
import {
  Button,
  Dialog,
  Inline,
  Stack,
  Strong,
  Text,
  useToast,
} from 'braid-design-system';
import { useId, type FC } from 'react';

import { TrackOnMount } from 'src/libs/Analytics';
import { ConnectionPlugIllustration } from 'src/libs/Illustrations';

import translations from './.vocab';
import { useUnlockReferenceCheckMutation } from './UnlockReferenceCheckDialog.hirer.generated';

interface UnlockReferenceCheckDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  goToReport: () => void;
  remainingCount: number;
  candidateDetails: {
    candidateFirstName: string;
    applicationCorrelationId: string;
  };
  onCompleted: () => void;
  onClickUpgradeReferenceButton: () => void;
  onMount: () => void;
}

export const UnlockReferenceCheckDialog: FC<
  UnlockReferenceCheckDialogProps
> = ({
  open,
  onClose,
  candidateDetails,
  remainingCount,
  jobId,
  goToReport,
  onCompleted,
  onClickUpgradeReferenceButton,
  onMount,
}) => {
  const { t } = useTranslations(translations);
  const showToast = useToast();

  const id = useId();
  const [unlockReferenceCheck, unlockReferenceCheckResult] =
    useUnlockReferenceCheckMutation();

  const handleOnClose = () => {
    onClose();
  };

  const handleUnlockReferenceCheck = () => {
    onClickUpgradeReferenceButton();
    unlockReferenceCheck({
      variables: {
        input: {
          jobId,
          applicationCorrelationId: candidateDetails.applicationCorrelationId,
        },
      },
      onCompleted: (result) => {
        if (
          result.unlockReferenceCheck.__typename ===
            'UnlockReferenceCheckResponseSuccess' &&
          result.unlockReferenceCheck.success
        ) {
          onCompleted();
          showToast({
            message: t('Reference check upgraded'),
            closeLabel: t('Close'),
            tone: 'positive',
            action: {
              label: t('View the report'),
              onClick: () => {
                goToReport();
              },
            },
          });
          onClose();
        }
      },
    });
  };

  const onCloseError = () => {
    unlockReferenceCheckResult.reset();
    onClose();
  };

  if (
    unlockReferenceCheckResult.error ||
    unlockReferenceCheckResult.data?.unlockReferenceCheck.__typename ===
      'ResponseError'
  ) {
    return (
      <Dialog
        id={id}
        open
        onClose={onCloseError}
        closeLabel={t('Close')}
        title={t("We couldn't upgrade references for {firstName}", {
          firstName: candidateDetails.candidateFirstName,
        })}
        illustration={<ConnectionPlugIllustration height={128} width={128} />}
      >
        <Stack space="large" align="center">
          <Text>{t('Please refresh the page or try again later.')}</Text>
          <Inline space="small">
            <Button onClick={onCloseError} variant="solid" tone="brandAccent">
              {t('Close')}
            </Button>
          </Inline>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      id={id}
      title={t('Upgrade to standard report?')}
      open={open}
      onClose={handleOnClose}
      closeLabel={t('Close')}
    >
      <TrackOnMount onMount={onMount}>
        <Stack space={['large', 'xlarge']}>
          <Stack space="large">
            <Text>
              {t(
                "You're upgrading <Strong>{firstName}'s</Strong> limited reference check.",
                {
                  firstName: candidateDetails.candidateFirstName,
                  Strong: (children) => <Strong>{children}</Strong>,
                },
              )}
            </Text>
            <Text>
              {t(
                "You'll have <Strong>{remainingCount} reference checks left</Strong> after this.",
                {
                  remainingCount,
                  Strong: (children) => <Strong>{children}</Strong>,
                },
              )}
            </Text>
          </Stack>
          <Inline space="medium" align="left" collapseBelow="tablet" reverse>
            <Button
              variant="solid"
              tone="brandAccent"
              onClick={handleUnlockReferenceCheck}
              loading={unlockReferenceCheckResult.loading}
            >
              {t('Upgrade references')}
            </Button>

            <Button variant="soft" onClick={handleOnClose}>
              {t('Cancel')}
            </Button>
          </Inline>
        </Stack>
      </TrackOnMount>
    </Dialog>
  );
};
